import { useState } from 'react'
import { motion } from 'framer-motion'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, MessageSquare, Heart, Sparkles, PartyPopper, Gift } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSite } from '../../context/SiteContext'
import { useContactForm, usePrayerForm } from '../../hooks/useForm'
import { contactSchema, prayerSchema } from '../../utils/validators'
import { Input, Textarea, Checkbox } from '../../components/common'

const ContactForm = () => {
  const { submit, submitting, submitted, error, reset } = useContactForm()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm({
    resolver: zodResolver(contactSchema),
  })

  const onSubmit = async (data) => {
    const result = await submit(data)
    if (result.success) {
      resetForm()
    }
  }

  if (submitted) {
    return (
      <motion.div
        className="text-center py-12"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
        >
          <PartyPopper className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-2xl font-heading font-bold text-dark-100 mb-2">Message Sent!</h3>
        <p className="text-dark-300 mb-6">Thank you for reaching out. We'll get back to you soon.</p>
        <motion.button
          onClick={reset}
          className="px-6 py-3 rounded-xl border-2 border-primary-500 text-primary-600 font-semibold hover:bg-primary-50 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Send Another Message
        </motion.button>
      </motion.div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="Your Name"
          required
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Email Address"
          type="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="Phone (optional)"
          type="tel"
          {...register('phone')}
        />
        <Input
          label="Subject"
          required
          error={errors.subject?.message}
          {...register('subject')}
        />
      </div>

      <Textarea
        label="Message"
        rows={5}
        required
        error={errors.message?.message}
        {...register('message')}
        placeholder="How can we help you?"
      />

      {error && (
        <motion.p
          className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {error}
        </motion.p>
      )}

      <motion.button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold shadow-lg shadow-primary-500/25 disabled:opacity-50"
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Send className="w-5 h-5" />
        )}
        Send Message
      </motion.button>
    </form>
  )
}

const PrayerForm = () => {
  const { submit, submitting, submitted, error, reset } = usePrayerForm()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm({
    resolver: zodResolver(prayerSchema),
    defaultValues: {
      isConfidential: false,
    },
  })

  const onSubmit = async (data) => {
    const result = await submit(data)
    if (result.success) {
      resetForm()
    }
  }

  if (submitted) {
    return (
      <motion.div
        className="text-center py-12"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          className="w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
        >
          <Heart className="w-10 h-10 text-white" fill="currentColor" />
        </motion.div>
        <h3 className="text-2xl font-heading font-bold text-dark-100 mb-2">Prayer Request Received</h3>
        <p className="text-dark-300 mb-6">Our prayer team will be lifting you up in prayer.</p>
        <motion.button
          onClick={reset}
          className="px-6 py-3 rounded-xl border-2 border-secondary-500 text-secondary-600 font-semibold hover:bg-secondary-50 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Submit Another Request
        </motion.button>
      </motion.div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="Your Name"
          required
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Email Address"
          type="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
      </div>

      <Textarea
        label="Your Prayer Request"
        rows={5}
        required
        error={errors.prayerRequest?.message}
        {...register('prayerRequest')}
        placeholder="Share your prayer request with us..."
      />

      <Checkbox
        label="Keep this prayer request confidential (shared only with pastoral staff)"
        {...register('isConfidential')}
      />

      {error && (
        <motion.p
          className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {error}
        </motion.p>
      )}

      <motion.button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-bold shadow-lg shadow-secondary-500/25 disabled:opacity-50"
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Heart className="w-5 h-5" />
        )}
        Submit Prayer Request
      </motion.button>
    </form>
  )
}

const Contact = () => {
  const [activeTab, setActiveTab] = useState('contact')
  const { settings, getFullAddress, getGoogleMapsUrl } = useSite()

  const contactCards = [
    {
      icon: MapPin,
      title: 'Visit Us',
      content: getFullAddress(),
      action: { label: 'Get Directions', href: getGoogleMapsUrl() },
      gradient: 'from-primary-500 to-blue-500',
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: settings.phone,
      action: { label: 'Call Now', href: `tel:${settings.phone}` },
      gradient: 'from-secondary-500 to-purple-500',
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: settings.email,
      action: { label: 'Send Email', href: `mailto:${settings.email}` },
      gradient: 'from-accent-500 to-orange-500',
    },
    {
      icon: Clock,
      title: 'Office Hours',
      content: settings.officeHours,
      action: null,
      gradient: 'from-emerald-500 to-teal-500',
    },
  ]

  const giveUrl = 'https://graceledger-production.up.railway.app/give'

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <MessageSquare className="w-4 h-4 text-secondary-400" />
              Get In Touch
            </motion.span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
              Contact <span className="bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">Us</span>
            </h1>
            <p className="text-xl text-white/70">
              Have a question or need prayer? We'd love to hear from you. Reach out today!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8 -mt-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactCards.map((item, index) => (
              <motion.div
                key={item.title}
                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all text-center group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <motion.div
                  className={`w-14 h-14 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <item.icon className="w-7 h-7 text-white" />
                </motion.div>
                <h3 className="font-heading font-bold text-dark-100 mb-2">{item.title}</h3>
                <p className="text-dark-300 text-sm mb-3">{item.content}</p>
                {item.action && (
                  <a
                    href={item.action.href}
                    target={item.action.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.action.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-primary-600 hover:text-primary-700 text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                  >
                    {item.action.label}
                    <Sparkles className="w-3 h-3" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Give Banner */}
      <section className="py-12 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.a
            href={giveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="relative bg-gradient-to-r from-secondary-600 via-secondary-500 to-primary-500 rounded-3xl p-8 md:p-12 overflow-hidden shadow-2xl shadow-secondary-500/30">
              {/* Animated background */}
              <div className="absolute inset-0 overflow-hidden">
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -top-10 -right-10 w-64 h-64 bg-white/20 rounded-full blur-3xl"
                />
                <motion.div
                  animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute -bottom-10 -left-10 w-64 h-64 bg-accent-500/30 rounded-full blur-3xl"
                />
              </div>

              <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                    className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center"
                  >
                    <Gift className="w-10 h-10 text-white" />
                  </motion.div>
                  <div className="text-center md:text-left">
                    <h3 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
                      Support Our Ministry
                    </h3>
                    <p className="text-white/90 text-lg">
                      Your generosity helps us spread the Gospel and serve our community
                    </p>
                  </div>
                </div>

                <motion.div
                  className="flex items-center gap-3 px-8 py-4 bg-white rounded-2xl text-secondary-600 font-bold text-xl shadow-xl"
                  whileHover={{ scale: 1.05 }}
                  animate={{
                    boxShadow: [
                      '0 0 0 0 rgba(255, 255, 255, 0.4)',
                      '0 0 0 15px rgba(255, 255, 255, 0)',
                      '0 0 0 0 rgba(255, 255, 255, 0)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Heart className="w-6 h-6 fill-current" />
                  GIVE NOW
                </motion.div>
              </div>
            </div>
          </motion.a>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-gradient-to-b from-white to-light-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Map */}
            <div className="lg:col-span-1">
              <motion.div
                className="sticky top-24"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-soft h-64 lg:h-80 mb-6 border-4 border-white">
                  <iframe
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(getFullAddress())}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Church Location"
                  />
                </div>

                <div className="bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 rounded-2xl p-6 border border-primary-100">
                  <h3 className="font-heading font-bold text-lg text-dark-100 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary-600" />
                    Service Times
                  </h3>
                  <div className="space-y-3">
                    {settings.serviceTimes?.map((service, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-dark-300 text-sm">{service.name}</span>
                        <span className="font-medium text-dark-100 text-sm bg-white px-3 py-1 rounded-lg">
                          {service.day} {service.time}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                {/* Tab Navigation */}
                <div className="flex gap-3 mb-8 p-1.5 bg-light-100 rounded-2xl w-fit">
                  <motion.button
                    onClick={() => setActiveTab('contact')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                      activeTab === 'contact'
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
                        : 'text-dark-300 hover:text-dark-100'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Mail className="w-5 h-5" />
                    Contact Us
                  </motion.button>
                  <motion.button
                    onClick={() => setActiveTab('prayer')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                      activeTab === 'prayer'
                        ? 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-white shadow-lg'
                        : 'text-dark-300 hover:text-dark-100'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Heart className="w-5 h-5" />
                    Prayer Request
                  </motion.button>
                </div>

                {/* Form Content */}
                <div className="bg-white rounded-3xl shadow-soft p-8 border border-gray-100">
                  {activeTab === 'contact' ? (
                    <>
                      <div className="mb-8">
                        <h2 className="text-3xl font-heading font-bold text-dark-100 mb-2">
                          Send Us a Message
                        </h2>
                        <p className="text-dark-300">
                          Fill out the form below and we'll get back to you as soon as possible.
                        </p>
                      </div>
                      <ContactForm />
                    </>
                  ) : (
                    <>
                      <div className="mb-8">
                        <h2 className="text-3xl font-heading font-bold text-dark-100 mb-2">
                          Submit a Prayer Request
                        </h2>
                        <p className="text-dark-300">
                          Share your prayer needs with us. Our prayer team is ready to lift you up.
                        </p>
                      </div>
                      <PrayerForm />
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Contact
